import { useQuery } from '@tanstack/react-query'
import { checkTransactionStatus, TransactionStatusResponseDTO } from 'api/simple_payment'
import { getTicketStatuses } from 'api/ticket'
import { SeatStatus } from 'types/Location'
import { SeatStatusArray } from 'types/Ticket'
import { TransactionStatusEnum } from 'types/TransactionStatus'

export const useTransactionStatusInterval = () => {
    const transactionId = sessionStorage.getItem('transaction_id')

    const { data, error } = useQuery<TransactionStatusResponseDTO>({
        queryKey: ['transactionStatus', transactionId],
        queryFn: async () => {
            if (!transactionId) throw new Error('No transaction ID found')
            return checkTransactionStatus(transactionId)
        },
        refetchInterval: 2000,
        refetchIntervalInBackground: true,
        enabled: !!transactionId,
    })

    const status = data?.status as TransactionStatusEnum | undefined
    const returnedTransactionId = data?.transactionId

    const isPaymentFinished = returnedTransactionId === transactionId && status === TransactionStatusEnum.Finished

    const isPendingStatus = status === TransactionStatusEnum.InPayment || status === TransactionStatusEnum.InFraud

    const isErrorStatus = [TransactionStatusEnum.Timeout, TransactionStatusEnum.NotAuthorized, TransactionStatusEnum.Fraud].includes(
        status ?? ('' as TransactionStatusEnum),
    )

    const isCancelledStatus = status === TransactionStatusEnum.Cancelled

    return {
        isPaymentFinished,
        isPendingStatus,
        isErrorStatus,
        isCancelledStatus,
        transactionId: returnedTransactionId,
        status,
        error,
    }
}

export const useTicketStatusesInterval = (seatIds: string[], eventId: string) => {
    const { data: statusesArray, error } = useQuery<SeatStatusArray>({
        queryKey: ['ticketStatuses', seatIds, eventId],
        queryFn: () => getTicketStatuses(seatIds, eventId),
        refetchInterval: 2000,
        refetchIntervalInBackground: true,
        enabled: seatIds.length > 0,
    })

    // Check if all tickets sold
    const areAllTicketsSold = statusesArray?.every((status) => status.status === SeatStatus.Sold) ?? false

    // Check if all tickets are in payment (pending)
    const areAnyTicketsInPayment = statusesArray?.some((status) => status.status === SeatStatus.InPayment) ?? false

    // Check if any ticket has failed or other status (not Sold or InPayment)
    const isAnyFailed =
        statusesArray?.some((status) => status.status !== SeatStatus.Sold && status.status !== SeatStatus.InPayment) ?? false

    return { statusesArray, areAllTicketsSold, areAnyTicketsInPayment, isAnyFailed, error }
}
